<template>
    <div class="fixed bottom-0 inset-x-0 px-4 pb-4 sm:inset-0 sm:flex sm:items-start mt-20 sm:justify-center" style="z-index:999999;">
        <div class="fixed inset-0 transition-opacity" @click="$emit('close')">
            <div class="absolute inset-0 bg-black opacity-75"></div>
        </div>
        <div class="bg-white overflow-y-auto shadow-xl transform transition-all w-1/2 max-h-full" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
            <div class="bg-white px-4 pt-5 pb-4 sm:p-4 sm:pb-4">
                <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                    <button type="button" class="text-gray-500 hover:text-gray-600 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150" aria-label="Close" @click="$emit('close')">
                        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="sm:flex sm:items-start border-b border-gray-400">
                    <div class="mt-3 text-center sm:mt-0 sm:text-left">
                        <h3 class="text-lg leading-6 font-medium text-black pb-2" id="modal-headline">
                            <slot name="heading">Send Email</slot>
                        </h3>
                    </div>
                </div>
                <ValidationObserver ref="form" v-slot="{ invalid }">
                    <div class="flex md:flex-row sm:flex-col">
                        <div class="w-full mt-2 grid grid-cols-1 row-gap-1 col-gap-1 sm:grid-cols-12">
                            <div class="col-span-12 bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
                            <div class="col-span-12 bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>
                            <div class="col-span-1 text-xs py-2 font-bold">
                                From:
                            </div>
                            <div class="col-span-11 text-sm bg-gray-200 border border-gray-400 flex flex-wrap items-center relative">
                                <span v-if="mailFrom" class="px-2 text-xs cursor-default">{{ mailFrom }}</span>
                                <span v-else-if="$store.getters.emailProvider" class="px-2 text-xs cursor-default">{{ $store.getters.fullName }} &lt;{{ $store.getters.emailProvider.email }}&gt;</span>
                                <span v-else class="px-2 text-xs cursor-default">You need to connect your mail account before sending emails.</span>
                            </div>
                            <div class="col-span-1 text-xs py-2 font-bold" :class="{ 'opacity-50': !$store.getters.emailProvider }">
                                To:
                            </div>
                            <div class="col-span-11 text-sm bg-white border border-gray-400 flex flex-wrap items-center relative" :class="{ 'opacity-50': !$store.getters.emailProvider }">
                                <span class="text-gray-900 bg-gray-300 py-1 px-2 mr-1 inline-block relative text-xs m-1" v-for="(to, index) in tos" :key="index">
                                    <span v-if="to.name">{{ to.name }} &lt;{{ to.email }}&gt;</span>
                                    <span v-else-if="to.email">{{ to.email }}</span>
                                    <a href="javascript:void(0)" class="ml-2" @click.prevent="removeEmail('tos', index)" v-if="!to.locked">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fal" data-icon="times" class="svg-inline--fa fa-times fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                            <path fill="currentColor" d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                                        </svg>
                                    </a>
                                </span>
                                <input type="text"
                                    class="flex-1 outline-none focus:outline-none py-1 px-1 text-xs"
                                    data-ref="tos"
                                    v-model="tosInput"
                                    @keydown.8="removeLastEmail"
                                    @keydown.enter.prevent="emailFromInput('tos')"
                                    @blur="emailFromInput('tos')"
                                    :readonly="!$store.getters.emailProvider"
                                >
                            </div>
                            <div class="col-span-1 text-xs py-2 font-bold" v-if="showCC">
                                Cc:
                            </div>
                            <div class="col-span-11 text-sm bg-white border border-gray-400 flex flex-wrap items-center relative" v-if="showCC">
                                <span class="text-gray-900 bg-gray-300 py-1 px-2 mr-1 inline-block relative text-xs m-1" v-for="(cc, index) in ccs" :key="index">
                                    <span v-if="cc.name">{{ cc.name }} &lt;{{ cc.email }}&gt;</span>
                                    <span v-else-if="cc.email">{{ cc.email }}</span>
                                    <a href="javascript:void(0)" class="ml-2" @click.prevent="removeEmail('ccs', index)" v-if="!cc.locked">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fal" data-icon="times" class="svg-inline--fa fa-times fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                            <path fill="currentColor" d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                                        </svg>
                                    </a>
                                </span>
                                <input type="text"
                                    class="flex-1 outline-none focus:outline-none py-1 px-1 text-xs"
                                    data-ref="ccs"
                                    v-model="ccsInput"
                                    @keydown.8="removeLastEmail"
                                    @keydown.enter.prevent="emailFromInput('ccs')"
                                    :readonly="!$store.getters.emailProvider"
                                >
                            </div>
                            <div class="col-span-1 text-xs py-2 font-bold" v-if="showBCC">
                                Bcc:
                            </div>
                            <div class="col-span-11 text-sm bg-white border border-gray-400 flex flex-wrap items-center relative" v-if="showBCC">
                                <span class="text-gray-900 bg-gray-300 py-1 px-2 mr-1 inline-block relative text-xs m-1" v-for="(bcc, index) in bccs" :key="index">
                                    <span v-if="bcc.name">{{ bcc.name }} &lt;{{ bcc.email }}&gt;</span>
                                    <span v-else-if="bcc.email">{{ bcc.email }}</span>
                                    <a href="javascript:void(0)" class="ml-2" @click.prevent="removeEmail('ccs', index)" v-if="!bcc.locked">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fal" data-icon="times" class="svg-inline--fa fa-times fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                            <path fill="currentColor" d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                                        </svg>
                                    </a>
                                </span>
                                <input type="text"
                                    class="flex-1 outline-none focus:outline-none py-1 px-1 text-xs"
                                    data-ref="bccs"
                                    v-model="bccsInput"
                                    @keydown.8="removeLastEmail"
                                    @keydown.enter.prevent="emailFromInput('bccs')"
                                    :readonly="!$store.getters.emailProvider"
                                >
                            </div>
                            <div class="col-span-1 text-xs py-2 font-bold" :class="{ 'opacity-50': !$store.getters.emailProvider }">
                                Subject:
                            </div>
                            <div class="col-span-11 text-sm bg-white border border-gray-400 flex flex-wrap items-center relative" :class="{ 'opacity-50': !$store.getters.emailProvider }">
                                <input type="text"
                                    class="flex-1 outline-none focus:outline-none py-1 px-1 text-xs"
                                    v-model="mailSubject"
                                    :readonly="!$store.getters.emailProvider">
                            </div>
                            <div class="col-span-12 text-xs pb-1 font-bold flex flex-row items-center" v-if="attachments.length > 0">
                                <span class="mr-1">Attachments:</span>
                                <span class="text-gray-900 bg-gray-300 py-1 px-2 mr-1 inline-block relative text-xs m-1 font-light" v-for="(attachment, index) in attachments" :key="index">
                                    <span>{{ attachment.filename }}</span>
                                    <a href="javascript:void(0)" class="ml-2" @click.prevent="removeAttachment(attachment, index)">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fal" data-icon="times" class="svg-inline--fa fa-times fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                            <path fill="currentColor" d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                                        </svg>
                                    </a>
                                </span>
                            </div>
                            <wysiwyg class="sm:col-span-12 email-editor mt-2 text-xs"
                                :class="{ 'opacity-50': !$store.getters.emailProvider }"
                                v-model="mailBody" />
                        </div>
                    </div>
                    <div class="sm:flex sm:items-start border-b border-gray-400 mt-6" v-if="includeSms">
                        <div class="mt-3 text-center sm:mt-0 sm:text-left">
                            <h3 class="text-lg leading-6 font-medium text-black pb-2" id="modal-sub-headline">
                                <slot name="sms-heading">Send SMS</slot>
                            </h3>
                        </div>
                    </div>
                    <div class="flex md:flex-row sm:flex-col" v-if="includeSms">
                        <div class="w-full mt-2 grid grid-cols-1 row-gap-1 col-gap-4 sm:grid-cols-12">
                            <div class="col-span-12 bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
                            <div class="col-span-12 bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>
                            <div class="col-span-12 text-sm"><strong class="pr-2">To:</strong> {{ smsTo.name }} ({{ smsTo.number }})</div>
                            <input-textarea class="sm:col-span-12 text-xs text-black"
                                placeholder="Message"
                                name="message"
                                v-model="smsBody"
                                :rows="2"
                                :input-style="'color: #000000!important; font-size: 12px;'" />
                        </div>
                    </div>
                </ValidationObserver>
            </div>
            <div class="flex w-full" v-if="$store.getters.emailProvider">
                <div class="bg-gray-200 px-4 py-3 sm:px-4 sm:flex sm:flex-row flex-grow">
                    <span class="flex w-full rounded-md shadow-sm sm:w-auto">
                        <button type="button" class="inline-flex justify-center w-full border border-gray-500 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-900 shadow-sm focus:outline-none transition ease-in-out duration-150 sm:text-sm sm:leading-5 mr-2 opacity-50"
                            :class="{ 'opacity-100': showCC }"
                            @click="showCC = !showCC">
                            CC
                        </button>
                    </span>
                    <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                        <button type="button" class="inline-flex justify-center w-full border border-gray-500 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-900 shadow-sm focus:outline-none transition ease-in-out duration-150 sm:text-sm sm:leading-5 mr-2 opacity-50"
                            :class="{ 'opacity-100': showBCC }"
                            @click="showBCC = !showBCC">
                            BCC
                        </button>
                    </span>
                    <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                        <button type="button" class="inline-flex justify-center w-full border border-gray-500 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-900 shadow-sm focus:outline-none transition ease-in-out duration-150 sm:text-sm sm:leading-5 mr-2 opacity-100"
                            @click="addFiles()">
                            <font-awesome-icon class="text-gray-900 text-sm mt-1 mr-1" :icon="['fal', 'paperclip']" v-if="!uploading"/><span v-if="!uploading">Add Attachment(s)</span>
                            <font-awesome-icon class="text-gray-900 text-sm mt-1 mr-1 fa-spin" :icon="['fal', 'spinner']" v-if="uploading"/><span v-if="uploading">Uploading...</span>
                        </button>
                    </span>
                </div>
                <div class="bg-gray-200 px-4 py-3 sm:px-4 sm:flex sm:flex-row-reverse flex-grow">
                    <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
                        <button type="button" class="inline-flex justify-center w-full border border-transparent px-4 py-2 bg-default-500 text-base leading-6 font-medium text-white shadow-sm hover:bg-default-500 focus:outline-none transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                            :class="[ tos.length === 0 || mailSubject === '' || uploading ? 'opacity-50 cursor-not-allowed' : '' ]"
                            @click="submit()"
                            :disabled="tos.length === 0 || mailSubject === '' || uploading">
                            <slot name="submit-button">Send</slot>
                        </button>
                    </span>
                    <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                        <button type="button" class="inline-flex justify-center w-full border border-gray-500 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-900 shadow-sm hover:bg-gray-300 focus:outline-none transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                            @click="$emit('close')">
                            Cancel
                        </button>
                    </span>
                </div>
            </div>
        </div>
        <input class="absolute" style="left: -99999px;"
            type="file"
            id="files"
            name="files"
            :multiple="true"
            ref="files"
            @change="handleUpload()"
            :disabled="disabled"
            :accept="'*'" />
    </div>
</template>

<script>
import axios from "axios";
import Email from "@/mixins/Email";

export default {
    name: "EmailModal",
    mixins: [ Email ],
    props: {
        to: { required: false, default: null },
        from: { required: false, default: null },
        subject: { required: false, default: null },
        model: { required: false, default: null },
        id: { required: false, default: null },
        includeSms: { required: false, default: false },
        smsTo: { required: false, default: null },
        content: { required: false, default: '' },
        smsContent: { required: false, default: '' },
        isQuote: { required: false, default: false },
        isInvoice: { required: false, default: false },
        initialAttachments: { required: false, default: null }
    },
    mounted() {
        document.body.className = 'overflow-hidden';

        if (this.from){
            this.mailFrom = this.from;
        }

        if (this.subject){
            this.mailSubject = this.subject;
        }

        if (this.smsContent){
            this.smsBody = this.smsContent;
        }

        if (this.initialAttachments){
            this.attachments = this.initialAttachments;
        }
    },
    data: function () {
        return {
            item: {},
            submitted: false,
            errorMessage: '',
            smsBody: ''
        }
    },
    methods: {
        submit: function () {
            this.submitted = false;

            this.$refs.form.validate().then(success => {
                if (!success) {
                    this.submitted = true;
                    return;
                }

                this.$store.commit('TOGGLE_BACKDROP', {open: true});

                this.submitted = true;
                this.errorMessage = '';

                let _url = process.env.VUE_APP_API_URL + '/emails/send';
                let _method = 'POST';

                axios({
                    method: _method,
                    url: _url,
                    data: {
                        to: this.tos,
                        cc: this.ccs,
                        bcc: this.bccs,
                        subject: this.mailSubject,
                        body: this.mailBody,
                        from: this.mailFrom ? {
                            id: this.$store.getters.user.id,
                            provider: 'system',
                            name: this.mailFrom.name,
                            email: this.mailFrom.email
                        } : {
                            id: this.$store.getters.user.id,
                            provider: this.$store.getters.emailProvider.provider,
                            name: this.$store.getters.fullName,
                            email: this.$store.getters.emailProvider.email
                        },
                        attachments: this.attachments.map(attachment => {
                            return { id: attachment.id, path: attachment.path, filename: attachment.filename };
                        }),
                        model: this.model,
                        id: this.id,
                        sms : {
                            send: this.includeSms,
                            to: this.smsTo,
                            message: this.smsBody
                        },
                        isQuote: this.isQuote,
                        isInvoice: this.isInvoice
                    }
                }).then(response => {
                    this.$store.commit('TOGGLE_BACKDROP', {open: false});

                    if (response.data.success) {
                        this.$notify({
                            group: 'main',
                            type: 'success',
                            title: 'Success',
                            text: response.data.message,
                            duration: 5000
                        });

                        this.$emit('sent');
                    } else {
                        if (response.data.message) {
                            this.errorMessage = response.data.message;
                        }

                        if (response.data.reAuth){
                            window.open('https://login.microsoftonline.com/common/oauth2/v2.0/authorize?' +
                                'client_id=f6b094e3-59c2-4134-9f24-5762cf194d7e' +
                                '&response_type=code' +
                                '&redirect_uri='+ process.env.VUE_APP_MICROSOFT_REDIRECT +
                                '&response_mode=query' +
                                '&access_type=offline' +
                                '&scope=https://graph.microsoft.com/offline_access https://graph.microsoft.com/User.Read https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/Mail.ReadWrite.Shared https://graph.microsoft.com/Mail.Send.Shared' +
                                '&prompt=consent');
                        }
                    }
                }).catch(error => {
                    this.$store.commit('TOGGLE_BACKDROP', {open: false});

                    if (error.response.data.message) {
                        this.errorMessage = error.response.data.message;
                    } else {
                        this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                    }
                });
            });
        }
    }
}
</script>

<style>
    .email-editor .ck.ck-editor__main > .ck-editor__editable {
        height: 40vh;
    }

    .email-editor .ck p {
        padding-bottom: 15px;
    }

    .email-editor .ck-content {
        color: #000000;
        font-size: 13px;
    }

    .editr p {
        padding-bottom: 15px;
    }
</style>
